import { useState } from 'react';

import { METHODS, type TestResult } from './types.js';

const INPUT =
  'rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100 ' +
  'placeholder:text-slate-600 focus:border-sky-500 focus:outline-none';

export interface RuleTesterProps {
  onTest: (probe: { method: string; path: string }) => Promise<TestResult>;
  /** Lets the parent highlight the winning row in the list. */
  onResult: (result: TestResult | null) => void;
}

/**
 * Answers "who would take this request?" without sending it. Nothing is
 * captured and the delay is reported rather than waited out, so you can poke
 * at the rules of an endpoint that is live and in use.
 */
export function RuleTester({ onTest, onResult }: RuleTesterProps) {
  const [method, setMethod] = useState('POST');
  const [path, setPath] = useState('/orders/42/pay');
  const [result, setResult] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const run = async (): Promise<void> => {
    setRunning(true);
    setError(null);

    try {
      const next = await onTest({ method, path });
      setResult(next);
      onResult(next);
    } catch (testError) {
      setResult(null);
      onResult(null);
      setError(testError instanceof Error ? testError.message : 'No se pudo probar.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <section className="grid gap-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <h3 className="text-sm font-semibold text-slate-200">Probador</h3>

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          void run();
        }}
      >
        <select
          aria-label="Método a probar"
          className={INPUT}
          value={method}
          onChange={(event) => setMethod(event.target.value)}
        >
          {METHODS.filter((verb) => verb !== '*').map((verb) => (
            <option key={verb} value={verb}>
              {verb}
            </option>
          ))}
        </select>

        <input
          aria-label="Ruta a probar"
          className={`${INPUT} flex-1 font-mono`}
          data-testid="tester-path"
          placeholder="/orders/42/pay"
          value={path}
          onChange={(event) => setPath(event.target.value)}
        />

        <button
          type="submit"
          disabled={running}
          className="rounded-md border border-sky-700 px-3 py-1 text-sm text-sky-300 hover:bg-sky-900/40 disabled:opacity-50"
        >
          {running ? 'Probando…' : 'Probar'}
        </button>
      </form>

      {error ? (
        <p className="text-sm text-rose-400" role="alert">
          {error}
        </p>
      ) : null}

      {result ? (
        <div className="grid gap-1 text-sm" data-testid="tester-result">
          {result.matched ? (
            <p className="text-slate-300">
              Responde la regla{' '}
              <code className="text-sky-300">
                {result.matched.method} {result.matched.pathPattern}
              </code>{' '}
              → <span className="font-mono">{result.response.status}</span>
              {result.response.delayMs ? ` tras ${result.response.delayMs} ms` : ''}
            </p>
          ) : (
            <p className="text-slate-300">
              No casa ninguna regla: responde la respuesta por defecto →{' '}
              <span className="font-mono">{result.response.status}</span>
            </p>
          )}

          {Object.keys(result.params).length > 0 ? (
            <p className="text-xs text-slate-500">
              Parámetros:{' '}
              {Object.entries(result.params)
                .map(([name, value]) => `${name}=${value}`)
                .join(', ')}
            </p>
          ) : null}

          <pre className="overflow-x-auto rounded bg-slate-950 p-2 font-mono text-xs text-slate-300">
            {result.response.body === undefined
              ? '(sin cuerpo)'
              : typeof result.response.body === 'string'
                ? result.response.body
                : JSON.stringify(result.response.body, null, 2)}
          </pre>
        </div>
      ) : null}
    </section>
  );
}
